import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  LOVListWithImageValue,
  LOVValue,
} from "../api/services/pageContent/models";
import { OptionsDataType } from "../components";
import { commonSiteCoreKeys } from "../helpers/constants/siteCoreKeys";
import { replaceHashWithValue } from "../helpers/utils";
import { SiteCoreContentSelector } from "../redux/slices/appState/appStateSelector";
import { KeyValueMap } from "../redux/slices/appState/models";

export const useSiteCore = () => {
  const { Title: pageTitle, Components: pageContent } = useSelector(
    SiteCoreContentSelector,
  );

  /*
   * To fetch the label from given key
   * Example - fetchLabel(SITE_CORE.clientSearchFormKey,"ClientManagement.SearchClient.CompanyName")
   */
  const fetchLabel = (parentKey: string, key: string): string => {
    if (pageContent?.[parentKey]?.Labels) {
      return (
        pageContent[parentKey].Labels.find(
          (el: { Key: string; Phrase: string }) => el.Key === key,
        )?.Phrase || ""
      );
    }
    return "";
  };

  const useFetchLabel = (parentKey: string) => {
    return (childKey: string) => fetchLabel(parentKey, childKey);
  };

  const fetchRichTexts = (parentKey: string, key: string): string => {
    if (pageContent?.[parentKey]?.RichTexts) {
      return (
        pageContent[parentKey].RichTexts.find(
          (el: { Key: string; Phrase: string }) => el.Key === key,
        )?.Value || ""
      );
    }
    return "";
  };

  /*
   * To fetch each component title key
   * Example - fetchComponentTitle(SITE_CORE.clientSearchFormKey)
   */
  const fetchComponentTitle = (key: string): string => {
    return pageContent?.[key]?.Title || "";
  };

  /*
   * To fetch dropdown list from the api and set as per out dropdown required type
   * Example - fetchDropdownValues(SITE_CORE.clientSearchFormKey,"ClientManagement.SearchClient.FilterByList",);
   */
  const fetchDropdownValues = (
    parentKey: string,
    key: string,
    optionByValue?: boolean, // this key will be use for result section and if dropdown will be by value only
  ): OptionsDataType[] => {
    if (pageContent?.[parentKey]?.LOVList) {
      const values =
        pageContent[parentKey].LOVList.find(
          (el: { Key: string; Values: LOVValue[] }) => el.Key === key,
        )?.Values || [];
      return values.map((data) => ({
        value: optionByValue ? data.Value : data.Key,
        label: data.Value,
      }));
    }
    return [];
  };

  /*
   * Fetch dropdown values using prefix key + suffix (country / market)
   * Falls back to "-Common" if specific key not found
   *
   * Example:
   * fetchDropdownValuesByPrefix(
   *   parentKey,
   *   "CreateProgramComponent.ParticipantInformation.AgeRangeList",
   *   "IT"
   * )
   */
  const fetchDropdownValuesByPrefix = (
    parentKey: string,
    prefixKey: string,
    suffixKey: string,
  ): OptionsDataType[] => {
    if (!pageContent?.[parentKey]?.LOVList) {
      return [];
    }

    const lovList = pageContent[parentKey].LOVList;

    const specificKey = `${prefixKey}-${suffixKey}`;
    const commonKey = `${prefixKey}-Common`;

    // 1️⃣ Try country-specific key first
    let matchedLov = lovList.find(
      (el: { Key: string }) => el.Key === specificKey,
    );

    // 2️⃣ Fallback to Common
    if (!matchedLov) {
      matchedLov = lovList.find((el: { Key: string }) => el.Key === commonKey);
    }

    if (!matchedLov?.Values) {
      return [];
    }

    return matchedLov.Values.map((data: LOVValue) => ({
      value: data.Key,
      label: data.Value,
    }));
  };

  /*
   * Fetch dropdown list key using prefix key + suffix (country / market)
   * Falls back to "-Common" if specific key not found
   *
   * Example:
   * fetchDropdownListKeyValueForCountrySpecific(
   *   parentKey,
   *   "CreateProgramComponent.ParticipantInformation.AgeRangeList",
   *   "IT"
   *  "AgeRange.50-54"
   * )
   */
  const fetchDropdownListKeyValueForCountrySpecific = (
    parentKey: string,
    prefixKey: string,
    suffixKey: string,
    key: string,
  ): string => {
    if (!pageContent?.[parentKey]?.LOVList) {
      return "";
    }

    const lovList = pageContent[parentKey].LOVList;

    const specificKey = `${prefixKey}-${suffixKey}`;
    const commonKey = `${prefixKey}-Common`;

    // 1️⃣ Try country-specific key first
    let matchedLov = lovList.find(
      (el: { Key: string }) => el.Key === specificKey,
    );

    // 2️⃣ Fallback to Common
    if (!matchedLov) {
      matchedLov = lovList.find((el: { Key: string }) => el.Key === commonKey);
    }

    if (!matchedLov?.Values) {
      return "";
    }

    return (
      matchedLov.Values.find((el: { Key: string }) => el.Key === key)?.Value ||
      ""
    );
  };

  /*
   * To fetch common label which are not in  appCommonLabels
   * Example - fetchCommonLabel("Common.EnterPlaceholderText")
   */
  const fetchCommonLabel = (key: string): string => {
    return (
      pageContent?.[commonSiteCoreKeys.commonComponentKey]?.Labels?.find(
        (el: { Key: string; Phrase: string }) => el.Key === key,
      )?.Phrase || ""
    );
  };

  /*
   * To fetch the image from LOVListWithImageValue
   */
  const fetchLabelValueFromLOVListWithImage = (
    parentKey: string,
    LovKey: string,
    key: string,
  ): LOVListWithImageValue | undefined => {
    if (pageContent?.[parentKey]?.LOVListWithImage) {
      const values =
        pageContent?.[parentKey]?.LOVListWithImage.find(
          (el: { Key: string; Values: LOVListWithImageValue[] }) => {
            return el.Key === LovKey;
          },
        )?.Values || [];

      return values?.find((data) => data.Key === key);
    }
  };

  /*
   * To fetch common dropdown list from the api and set as per out dropdown required type
   * Example - fetchCommonDropdownValues("Common.StatusList")
   */
  const fetchCommonDropdownValues = (
    key: string,
    optionByValue?: boolean, // this key will be use for result section and if dropdown will be by value only
  ): OptionsDataType[] => {
    if (pageContent?.[commonSiteCoreKeys.commonComponentKey]?.LOVList) {
      const values =
        pageContent[commonSiteCoreKeys.commonComponentKey].LOVList.find(
          (el: { Key: string; Values: LOVValue[] }) => el.Key === key,
        )?.Values || [];
      return values.map((data) => ({
        value: optionByValue ? data.Value : data.Key,
        label: data.Value,
      }));
    }
    return [];
  };

  /*
   * Created commonLabels which will return some common labels across the application
   * Example - {commonLabels.savedFilter}
   */
  const commonLabels = useMemo(
    () => ({
      inputPlaceHolder: fetchCommonLabel(commonSiteCoreKeys.inputPlaceHolder),
      selectPlaceHolder: fetchCommonLabel(commonSiteCoreKeys.selectPlaceHolder),
      monthPlaceHolder: fetchCommonLabel(commonSiteCoreKeys.monthPlaceHolder),
      yearPlaceHolder: fetchCommonLabel(commonSiteCoreKeys.yearPlaceHolder),
      selectAllPlaceHolder: fetchCommonLabel(
        commonSiteCoreKeys.selectAllPlaceHolder,
      ),
      declineAllPlaceHolder: fetchCommonLabel(
        commonSiteCoreKeys.declineAllPlaceHolder,
      ),
      savedFilter: fetchCommonLabel(commonSiteCoreKeys.savedFilter),
      saveFilter: fetchCommonLabel(commonSiteCoreKeys.saveFilter),
      clearAll: fetchCommonLabel(commonSiteCoreKeys.clearAll),
      search: fetchCommonLabel(commonSiteCoreKeys.search),
      edit: fetchCommonLabel(commonSiteCoreKeys.edit),
      view: fetchCommonLabel(commonSiteCoreKeys.view),
      viewMore: fetchCommonLabel(commonSiteCoreKeys.viewMore),
      go: fetchCommonLabel(commonSiteCoreKeys.go),
      minutes: fetchCommonLabel(commonSiteCoreKeys.minutes),
      goToPage: fetchCommonLabel(commonSiteCoreKeys.goToPage),
      noResult: fetchCommonLabel(commonSiteCoreKeys.noResult),
      searchCriteria: fetchCommonLabel(commonSiteCoreKeys.searchCriteria),
      save: fetchCommonLabel(commonSiteCoreKeys.save),
      cancel: fetchCommonLabel(commonSiteCoreKeys.cancel),
      upload: fetchCommonLabel(commonSiteCoreKeys.upload),
      cancelActionText: fetchCommonLabel(commonSiteCoreKeys.cancelActionText),
      viewLocale: fetchCommonLabel(commonSiteCoreKeys.viewLocale),
      close: fetchCommonLabel(commonSiteCoreKeys.close),
      update: fetchCommonLabel(commonSiteCoreKeys.update),
      confirmation: fetchCommonLabel(commonSiteCoreKeys.confirmation),
      telephonePlaceHolder: fetchCommonLabel(
        commonSiteCoreKeys.telephonePlaceHolder,
      ),
      emailPlaceHolder: fetchCommonLabel(commonSiteCoreKeys.emailPlaceHolder),
      yes: fetchCommonLabel(commonSiteCoreKeys.yes),
      no: fetchCommonLabel(commonSiteCoreKeys.no),
      of: fetchCommonLabel(commonSiteCoreKeys.of),
      delete: fetchCommonLabel(commonSiteCoreKeys.delete),
      confirm: fetchCommonLabel(commonSiteCoreKeys.confirm),
      collapseAll: fetchCommonLabel(commonSiteCoreKeys.collapseAll),
      expandAll: fetchCommonLabel(commonSiteCoreKeys.expandAll),
      selectedCountries: fetchCommonLabel(commonSiteCoreKeys.selectedCountries),
      someThingWentWrongPleaseTryAgainLater: fetchCommonLabel(
        commonSiteCoreKeys.someThingWentWrongPleaseTryAgainLater,
      ),
      rowsPerPage: fetchCommonLabel(commonSiteCoreKeys.rowsPerPage),
      createdDate: fetchCommonLabel(commonSiteCoreKeys.createdDate),
      brazilPlaceholder: fetchCommonLabel(commonSiteCoreKeys.brazilPlaceholder),
      frenchPlaceholder: fetchCommonLabel(commonSiteCoreKeys.frenchPlaceholder),
      japanesePlaceholder: fetchCommonLabel(
        commonSiteCoreKeys.japanesePlaceholder,
      ),
      manageLocale: fetchCommonLabel(commonSiteCoreKeys.manageLocale),
      locale: fetchCommonLabel(commonSiteCoreKeys.locale),
      warning: fetchCommonLabel(commonSiteCoreKeys.warning),
      loadingPleaseWait: fetchCommonLabel(commonSiteCoreKeys.loadingPleaseWait),
      errorsIdentified: fetchCommonLabel(commonSiteCoreKeys.errorsIdentified),
      ok: fetchCommonLabel(commonSiteCoreKeys.ok),
      dateInputPlaceHolder: fetchCommonLabel(
        commonSiteCoreKeys.dateInputPlaceHolder,
      ),
      continue: fetchCommonLabel(commonSiteCoreKeys.continue),
      tag: fetchCommonLabel(commonSiteCoreKeys.tag),
      notes: fetchCommonLabel(commonSiteCoreKeys.notes),
      noRecordsFound: fetchCommonLabel(commonSiteCoreKeys.noRecordsFound),
      export: fetchCommonLabel(commonSiteCoreKeys.export),
      exportToExcel: fetchCommonLabel(commonSiteCoreKeys.exportToExcel),
      parent: fetchCommonLabel(commonSiteCoreKeys.parent),
      next: fetchCommonLabel(commonSiteCoreKeys.next),
      restore: fetchCommonLabel(commonSiteCoreKeys.restore),
      cancelButtonActionText: fetchCommonLabel(
        commonSiteCoreKeys.cancelButtonActionText,
      ),
      closeButtonActionText: fetchCommonLabel(
        commonSiteCoreKeys.closeButtonActionText,
      ),
      saveButtonActionText: fetchCommonLabel(
        commonSiteCoreKeys.saveButtonActionText,
      ),
      waitSaving: fetchCommonLabel(commonSiteCoreKeys.waitSaving),
      add: fetchCommonLabel(commonSiteCoreKeys.add),
      leavingPageActionText: fetchCommonLabel(
        commonSiteCoreKeys.leavingPageActionText,
      ),
      discardChangesText: fetchCommonLabel(
        commonSiteCoreKeys.discardChangesText,
      ),
      discardChangesGivenInfoWillLostText: fetchCommonLabel(
        commonSiteCoreKeys.discardChangesGivenInfoWillLostText,
      ),
      resetAllSearch: fetchCommonLabel(commonSiteCoreKeys.resetAllSearch),
      waitWorking: fetchCommonLabel(commonSiteCoreKeys.waitWorking),
      cancelChanges: fetchCommonLabel(commonSiteCoreKeys.cancelChanges),
      cancelButtonAlertText: fetchCommonLabel(
        commonSiteCoreKeys.cancelButtonAlertText,
      ),
      information: fetchCommonLabel(commonSiteCoreKeys.information),
      changesSavedMessage: fetchCommonLabel(
        commonSiteCoreKeys.changesSavedMessage,
      ),
      uploadingPleaseWait: fetchCommonLabel(
        commonSiteCoreKeys.uploadingPleaseWait,
      ),
      markAsActive: fetchCommonLabel(commonSiteCoreKeys.markAsActive),
      markAsExpired: fetchCommonLabel(commonSiteCoreKeys.markAsExpired),
      unmap: fetchCommonLabel(commonSiteCoreKeys.unmap),
      undoCancel: fetchCommonLabel(commonSiteCoreKeys.undoCancel),
      archive: fetchCommonLabel(commonSiteCoreKeys.archive),
      comment: fetchCommonLabel(commonSiteCoreKeys.comment),
      global: fetchCommonLabel(commonSiteCoreKeys.global),
      submit: fetchCommonLabel(commonSiteCoreKeys.submit),
      overWritingFilter: fetchCommonLabel(commonSiteCoreKeys.overWritingFilter),
      hierarchy: fetchCommonLabel(commonSiteCoreKeys.hierarchy),
      back: fetchCommonLabel(commonSiteCoreKeys.back),
      modify: fetchCommonLabel(commonSiteCoreKeys.modify),
      selected: fetchCommonLabel(commonSiteCoreKeys.selected),
      none: fetchCommonLabel(commonSiteCoreKeys.none),
      yesProceed: fetchCommonLabel(commonSiteCoreKeys.yesProceed),
      proceed: fetchCommonLabel(commonSiteCoreKeys.proceed),
      saveAndNextButtonText: fetchCommonLabel(
        commonSiteCoreKeys.saveAndNextButtonText,
      ),
      day: fetchCommonLabel(commonSiteCoreKeys.day),
      days: fetchCommonLabel(commonSiteCoreKeys.days),
      done: fetchCommonLabel(commonSiteCoreKeys.done),
      browse: fetchCommonLabel(commonSiteCoreKeys.browse),
      dropYourFile: fetchCommonLabel(commonSiteCoreKeys.dropYourFile),
      download: fetchCommonLabel(commonSiteCoreKeys.download),
      stateProvincePlaceHolder: fetchCommonLabel(
        commonSiteCoreKeys.stateProvincePlaceHolder,
      ),
      na: fetchCommonLabel(commonSiteCoreKeys.na),
      success: fetchCommonLabel(commonSiteCoreKeys.success),
      show: fetchCommonLabel(commonSiteCoreKeys.show),
      online: fetchCommonLabel(commonSiteCoreKeys.online),
      with: fetchCommonLabel(commonSiteCoreKeys.with),
      hour: fetchCommonLabel(commonSiteCoreKeys.hour),
      hours: fetchCommonLabel(commonSiteCoreKeys.hours),
      note: fetchCommonLabel(commonSiteCoreKeys.note),
      applyNecessaryFiltersMessage: fetchCommonLabel(
        commonSiteCoreKeys.applyNecessaryFiltersMessage,
      ),
      refineYourSearchInformationMessage: fetchCommonLabel(
        commonSiteCoreKeys.refineYourSearchInformationMessage,
      ),
      typeToSearchPlaceholder2Chars: fetchCommonLabel(
        commonSiteCoreKeys.typeToSearchPlaceholder2Chars,
      ),
      typeToSearchPlaceholder3Chars: fetchCommonLabel(
        commonSiteCoreKeys.typeToSearchPlaceholder3Chars,
      ),
      copied: fetchCommonLabel(commonSiteCoreKeys.copied),
      more: fetchCommonLabel(commonSiteCoreKeys.more),
      validate: fetchCommonLabel(commonSiteCoreKeys.validate),
      details: fetchCommonLabel(commonSiteCoreKeys.details),
      reOpen: fetchCommonLabel(commonSiteCoreKeys.reOpen),
      downloadInvoice: fetchCommonLabel(commonSiteCoreKeys.downloadInvoice),
      allRecords: fetchCommonLabel(commonSiteCoreKeys.allRecords),
      fineTuningSearchCriteriaMessage: fetchCommonLabel(
        commonSiteCoreKeys.fineTuningSearchCriteriaMessage,
      ),
    }),
    [pageContent],
  );

  const commonDropdownValues = useMemo(
    () => ({
      statusList: fetchCommonDropdownValues(commonSiteCoreKeys.statusList),
    }),
    [pageContent],
  );

  /*
   * fetchValidationMessage for fetch validation messages
   * replacement - is use for replace #key# with given value
   * Example - resolver: yupResolver(SearchProgramTemplateSchema(fetchValidationMessage)),
   * export const SearchProgramTemplateSchema = (fetchValidationMessage: (key: string) => string,) =>
        Yup.object().shape({
          Name: Yup.string().required(fetchValidationMessage("ValidationMessages.AlertMandatoryFields"))
        })
  */
  const fetchValidationMessage = (
    key: string,
    replacement?: KeyValueMap,
  ): string => {
    const message =
      pageContent?.[commonSiteCoreKeys.commonValidationKey]?.Labels?.find(
        (el: { Key: string; Phrase: string }) => el.Key === key,
      )?.Phrase || "";

    if (replacement) {
      return replaceHashWithValue(message, replacement);
    }

    return message;
  };

  /*
   * Created commonValidationMessages which will return some common validation across the application
   * Example - {commonValidationMessages.fieldRequiredFieldValidator}
   */
  const commonValidationMessages = useMemo(
    () => ({
      fieldRequiredFieldValidator: fetchValidationMessage(
        commonSiteCoreKeys.fieldRequiredFieldValidator,
      ),
      onlyAlphanumericSpacesAllowed: fetchValidationMessage(
        commonSiteCoreKeys.onlyAlphanumericSpacesAllowed,
      ),
      onlyAlphanumericAndSpaceAreAllowed: fetchValidationMessage(
        commonSiteCoreKeys.onlyAlphanumericAndSpaceAreAllowed,
      ),
      onlyNumericAndHyphenAreAllowed: fetchValidationMessage(
        commonSiteCoreKeys.onlyNumericAndHyphenAreAllowed,
      ),
      requiredValidation: fetchValidationMessage(
        commonSiteCoreKeys.requiredValidation,
      ),
      valueRangeFieldValidator1To99: fetchValidationMessage(
        commonSiteCoreKeys.valueRangeFieldValidator1To99,
      ),
      alphanumericFieldValidator: fetchValidationMessage(
        commonSiteCoreKeys.alphanumericFieldValidator,
      ),
      invalidEmail: fetchValidationMessage(commonSiteCoreKeys.invalidEmail),
      invalidLabelName: fetchValidationMessage(
        commonSiteCoreKeys.invalidLabelName,
      ),
      invalidPhoneNumber: fetchValidationMessage(
        commonSiteCoreKeys.invalidPhoneNumber,
      ),
      phoneNumberMinimumOf3DigitsRequiredMessage: fetchValidationMessage(
        commonSiteCoreKeys.phoneNumberMinimumOf3DigitsRequiredMessage,
      ),
      numberAndSpecialCharNotAllowed: fetchValidationMessage(
        commonSiteCoreKeys.numberAndSpecialCharNotAllowed,
      ),
      specialCharNotAllowed: fetchValidationMessage(
        commonSiteCoreKeys.specialCharNotAllowed,
      ),
      alphabetFieldValidator: fetchValidationMessage(
        commonSiteCoreKeys.alphabetFieldValidator,
      ),
      totalPercentageMustEqual100: fetchValidationMessage(
        commonSiteCoreKeys.totalPercentageMustEqual100,
      ),
      pleaseEnterOnlyPositiveNumber: fetchValidationMessage(
        commonSiteCoreKeys.pleaseEnterOnlyPositiveNumber,
      ),
      pleaseEnterNumberBetween1To500: fetchValidationMessage(
        commonSiteCoreKeys.pleaseEnterNumberBetween1To500,
      ),
      pleaseEnterNumberBetween1To600: fetchValidationMessage(
        commonSiteCoreKeys.pleaseEnterNumberBetween1To600,
      ),
      greaterThanZeroFieldValidator: fetchValidationMessage(
        commonSiteCoreKeys.greaterThanZeroFieldValidator,
      ),
      alphabetOrSpecialCharNotAllowed: fetchValidationMessage(
        commonSiteCoreKeys.alphabetOrSpecialCharNotAllowed,
      ),
      alphabetNotAllowedValidator: fetchValidationMessage(
        commonSiteCoreKeys.alphabetNotAllowedValidator,
      ),
      onlyAlphanumericSpacesAndHyphenAllowed: fetchValidationMessage(
        commonSiteCoreKeys.onlyAlphanumericSpacesAndHyphenAllowed,
      ),
      pleaseChooseAPastDate: fetchValidationMessage(
        commonSiteCoreKeys.pleaseChooseAPastDate,
      ),
      pleaseChooseAFutureDate: fetchValidationMessage(
        commonSiteCoreKeys.pleaseChooseAFutureDate,
      ),
      minimumOf20CharactersRequired: fetchValidationMessage(
        commonSiteCoreKeys.minimumOf20CharactersRequired,
      ),
      durationRequiredErrorMessage: fetchValidationMessage(
        commonSiteCoreKeys.durationRequiredErrorMessage,
      ),
      fieldContainsInvalidCharactersErrorMessage: fetchValidationMessage(
        commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
      ),
      invalidDate: fetchValidationMessage(commonSiteCoreKeys.invalidDate),
      fieldSpecialCharactersTooltipMessage: fetchValidationMessage(
        commonSiteCoreKeys.fieldSpecialCharactersTooltipMessage,
      ),
      allowedSpecialCharactersTitle: fetchValidationMessage(
        commonSiteCoreKeys.allowedSpecialCharacters,
      ),
    }),
    [pageContent],
  );

  const fetchBEValidationMessage = (
    key: string,
    replacement?: KeyValueMap | string,
  ): string => {
    const message =
      pageContent?.[commonSiteCoreKeys.commonBEValidationKey]?.Labels?.find(
        (el: { Key: string; Phrase: string }) => el.Key === key,
      )?.Phrase ?? "";

    if (replacement) {
      return replaceHashWithValue(message, replacement);
    }

    return message;
  };

  /*
   * To fetch each component any key
   * Example - fetchComponentKey(CreateProgramSiteCoreKeys.informationPopup,
   *            CreateProgramSiteCoreKeys.description)
   */
  const fetchComponentValueFromKey = (
    parentKey: string,
    key: string,
  ): string => {
    return (pageContent?.[parentKey]?.[
      key as keyof (typeof pageContent)[typeof parentKey]
    ] || "") as string;
  };

  /*  to fetch the medialist image from the given parentKey and mediaKey
  fetchMediaListImage(
       clientSearchSiteCoreKeys.result,
       clientSearchSiteCoreKeys.noHierarchyImage,
       clientSearchSiteCoreKeys.smallImage,
  );
  */

  const fetchMediaListImage = (
    parentKey: string,
    mediaKey: string,
    imageType: string,
  ) => {
    // Check if the MediaList exists in the specified parentKey
    if (pageContent?.[parentKey]?.MediaList) {
      // Find the media object from the MediaList by matching the mediaKey
      const mediaItem = pageContent[parentKey].MediaList.find(
        (el: { Key: string }) => el.Key === mediaKey,
      );

      // If the media item is found and the imageType exists (like SmallImage, MediumImage, etc.)
      if (mediaItem && mediaItem[imageType]) {
        return mediaItem[imageType]; // Return the respective image object (e.g., SmallImage, MediumImage)
      }
    }

    // Return null or empty if the conditions are not met
    return null;
  };

  // Function to fetch value by key with optimization using Map and useMemo
  const fetchValueByKeyOfDropdown = (
    masterKey: string,
    parentKey: string,
    key: string,
  ): string => {
    if (pageContent?.[masterKey]?.LOVList) {
      const lovList = pageContent[masterKey].LOVList;

      const lovListMap = new Map<string, Map<string, string>>(
        lovList.map((el: { Key: string; Values: LOVValue[] }) => [
          el.Key,
          new Map(el.Values.map((val) => [val.Key, val.Value])),
        ]),
      );

      // Access the value using the parentKey
      const parentValuesMap = lovListMap.get(parentKey);
      if (parentValuesMap) {
        return parentValuesMap.get(key) ?? "";
      }
    }
    return "";
  };
  // Function to fetch key by value with optimization using Map and useMemo
  const fetchKeyByValueOfDropdown = (
    masterKey: string,
    parentKey: string,
    value: string,
  ): string => {
    if (pageContent?.[masterKey]?.LOVList) {
      const lovList = pageContent[masterKey].LOVList;

      const lovListMap = new Map<string, Map<string, string>>(
        lovList.map((el: { Key: string; Values: LOVValue[] }) => [
          el.Key,
          new Map(el.Values.map((val) => [val.Value, val.Key])),
        ]),
      );

      // Access the key using the parentKey
      const parentValuesMap = lovListMap.get(parentKey);
      if (parentValuesMap) {
        return parentValuesMap.get(value) ?? "";
      }
    }
    return "";
  };

  /*
   * Function to fetch list of dropdown option by key with Backend api id
   */
  const fetchDropdownListValueByKey = (
    parentKey: string,
    key: string,
    apiData: { siteCoreKey: string; id: string }[],
  ): OptionsDataType[] => {
    if (pageContent?.[parentKey]?.LOVList) {
      const values =
        pageContent[parentKey].LOVList.find(
          (el: { Key: string; Values: LOVValue[] }) => el.Key === key,
        )?.Values || [];

      const result: OptionsDataType[] = [];
      apiData.forEach((data) => {
        const found = values.find((item) => item.Key === data.siteCoreKey);

        if (found) {
          result.push({
            label: found.Value,
            value: data.id,
          });
        }
      });
      return result;
    }

    return [];
  };

  /*
   * fetchTaxDetailsLabels for fetching tax-related labels
   */
  const fetchTaxDetailsLabels = (
    key: string,
    replacement?: KeyValueMap,
  ): string => {
    const message =
      pageContent?.[commonSiteCoreKeys.commonTaxDetailsKey]?.Labels?.find(
        (el: { Key: string; Phrase: string }) => el.Key === key,
      )?.Phrase ?? "";

    if (replacement) {
      return replaceHashWithValue(message, replacement);
    }

    return message;
  };

  /*
   * Created fetchTaxDetailsLabels which will return tax-related labels across the application
   * Example - fetchTaxDetailsLabels("TaxDetails.AddTaxHeading")
   */
  const commonTaxDetails = useMemo(
    () => ({
      taxDetails: fetchComponentTitle(commonSiteCoreKeys.taxDetails),
      addTaxHeading: fetchTaxDetailsLabels(commonSiteCoreKeys.addTaxHeading),
      tax: fetchTaxDetailsLabels(commonSiteCoreKeys.tax),
      percentage: fetchTaxDetailsLabels(commonSiteCoreKeys.taxPercentage),
      amount: fetchTaxDetailsLabels(commonSiteCoreKeys.taxAmount),
      totalTaxAmount: fetchTaxDetailsLabels(commonSiteCoreKeys.totalTaxAmount),
      itemDescriptionTableColumn: fetchTaxDetailsLabels(
        commonSiteCoreKeys.itemDescriptionTableColumn,
      ),
      percentageTableColumn: fetchTaxDetailsLabels(
        commonSiteCoreKeys.percentageTableColumn,
      ),
      amountTableColumn: fetchTaxDetailsLabels(
        commonSiteCoreKeys.amountTableColumn,
      ),
      actionTableColumn: fetchTaxDetailsLabels(
        commonSiteCoreKeys.actionTableColumn,
      ),
      noTaxAvailablePleaseAddTheRequiredTaxDetailsManually:
        fetchTaxDetailsLabels(
          commonSiteCoreKeys.noTaxAvailablePleaseAddTheRequiredTaxDetailsManually,
        ),
      areYouSureYouWantToDeleteTheTax: fetchTaxDetailsLabels(
        commonSiteCoreKeys.areYouSureYouWantToDeleteTheTax,
      ),
    }),
    [pageContent],
  );

  /*
   * fetchBillingConfigurationDetailsLabels for fetching billing configuration-related labels
   */
  const fetchBillingConfigurationDetailsLabels = (
    key: string,
    replacement?: KeyValueMap,
  ): string => {
    const message =
      pageContent?.[
        commonSiteCoreKeys.commonBillingConfigurationDetailsKey
      ]?.Labels?.find((el: { Key: string; Phrase: string }) => el.Key === key)
        ?.Phrase ?? "";

    if (replacement) {
      return replaceHashWithValue(message, replacement);
    }

    return message;
  };

  /*
   * Created commonBillingConfigurationDetails which will return billing configuration-related labels across the application
   * Example - fetchBillingConfigurationDetailsLabels("BillingConfigurationDetails.AddScheduleSubHeading")
   */
  const commonBillingConfigurationDetails = useMemo(
    () => ({
      billingConfigurationDetails: fetchComponentTitle(
        commonSiteCoreKeys.billingConfigurationDetails,
      ),
      configurationDetailsScheduledBasedHeading:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.configurationDetailsScheduledBasedHeading,
        ),
      configurationDetailsMilestoneBasedHeading:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.configurationDetailsMilestoneBasedHeading,
        ),
      schedulePeriod: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.schedulePeriod,
      ),
      value: fetchBillingConfigurationDetailsLabels(commonSiteCoreKeys.value),
      addScheduleSubHeading: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.addScheduleSubHeading,
      ),
      editScheduleSubHeading: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.editScheduleSubHeading,
      ),

      addMilestoneSubHeading: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.addMilestoneSubHeading,
      ),
      editMilestoneSubHeading: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.editMilestoneSubHeading,
      ),
      schedule: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.schedule,
      ),
      milestone: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.milestone,
      ),

      scheduleNumber: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.scheduleNumber,
      ),

      milestoneNumber: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.milestoneNumber,
      ),
      feePercentage: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.feePercentage,
      ),
      billingConfigurationAmount: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.billingConfigurationAmount,
      ),
      scheduleFrequency: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.scheduleFrequency,
      ),
      totalPercentage: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.totalPercentage,
      ),
      totalAmount: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.totalAmount,
      ),
      scheduleTableColumnHeading: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.scheduleTableColumnHeading,
      ),

      percentageTableColumnHeading: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.percentageTableColumnHeading,
      ),
      amountTableColumnHeading: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.amountTableColumnHeading,
      ),
      scheduleFrequencyTableColumnHeading:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.scheduleFrequencyTableColumnHeading,
        ),
      statusTableColumnHeading: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.statusTableColumnHeading,
      ),
      actionTableColumnHeading: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.actionTableColumnHeading,
      ),
      noMilestonesFoundMessage: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.noMilestonesFoundMessage,
      ),
      noSchedulesFoundMessage: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.noSchedulesFoundMessage,
      ),
      activities: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.activities,
      ),
      milestoneTableColumnHeading: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.milestoneTableColumnHeading,
      ),
      activitiesTableColumnHeading: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.activitiesTableColumnHeading,
      ),
      amountCannotGreaterThanProgramFeeErrorMessage:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.amountCannotGreaterThanProgramFeeErrorMessage,
        ),
      amountCannotGreaterThanProjectFeeErrorMessage:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.amountCannotGreaterThanProjectFeeErrorMessage,
        ),
      totalPercentageCannotExceed100: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.totalPercentageCannotExceed100,
      ),
      totalAmountMustEqualProgramFeeErrorMessage:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.totalAmountMustEqualProgramFeeErrorMessage,
        ),
      totalAmountMustEqualProjectFeeErrorMessage:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.totalAmountMustEqualProjectFeeErrorMessage,
        ),
      selectFrequencyThatFallsBetweenThePreviousAndNextSchedules:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.selectFrequencyThatFallsBetweenThePreviousAndNextSchedules,
        ),
      selectedFrequencyMustBeLowerThanThePreviouslyAddedSchedules:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.selectedFrequencyMustBeLowerThanThePreviouslyAddedSchedules,
        ),
      pleaseCreateMoreThanOneScheduleAndDistributeTheAmountAccordingly:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.pleaseCreateMoreThanOneScheduleAndDistributeTheAmountAccordingly,
        ),

      pleaseFillProgramFeeErrorMessage: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.pleaseFillProgramFeeErrorMessage,
      ),
      pleaseFillProjectFeeErrorMessage: fetchBillingConfigurationDetailsLabels(
        commonSiteCoreKeys.pleaseFillProjectFeeErrorMessage,
      ),
      areYouSureYouWantToDeleteTheMilestone:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.areYouSureYouWantToDeleteTheMilestone,
        ),
      areYouSureYouWantToDeleteTheSchedule:
        fetchBillingConfigurationDetailsLabels(
          commonSiteCoreKeys.areYouSureYouWantToDeleteTheSchedule,
        ),
    }),
    [pageContent],
  );

  return {
    fetchLabel,
    pageTitle,
    fetchCommonLabel,
    fetchCommonDropdownValues,
    commonLabels,
    commonDropdownValues,
    fetchDropdownValues,
    fetchDropdownValuesByPrefix,
    fetchDropdownListKeyValueForCountrySpecific,
    fetchComponentTitle,
    fetchValidationMessage,
    fetchBEValidationMessage,
    commonValidationMessages,
    fetchComponentValueFromKey,
    fetchRichTexts,
    fetchMediaListImage,
    fetchValueByKeyOfDropdown,
    fetchKeyByValueOfDropdown,
    useFetchLabel,
    commonTaxDetails,
    commonBillingConfigurationDetails,
    fetchDropdownListValueByKey,
    fetchLabelValueFromLOVListWithImage,
  };
};
